import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Volume2, Pause, Play, RotateCcw, Settings } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Aret'Hera Voice Playback Component
 * 
 * Provides text-to-speech read-aloud functionality for lesson content.
 * Features:
 * - OpenAI TTS "nova" voice (calm, nurturing)
 * - Sentence-level text highlighting
 * - Speed control (0.75x, 1x, 1.25x)
 * - Replay with auto-slowdown for better comprehension
 * - Pause between sentences for processing
 */

const AretHeraVoice = ({ 
  text, 
  blockId,
  autoPlay = false,
  onPlayStart,
  onPlayEnd,
  onSentenceChange,
  className = ""
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [speed, setSpeed] = useState(1.0);
  const [showSettings, setShowSettings] = useState(false);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(-1);
  const [sentences, setSentences] = useState([]);
  const [replayCount, setReplayCount] = useState(0);
  const [error, setError] = useState(null);
  
  const audioRef = useRef(null);
  const sentenceTimersRef = useRef([]);
  
  // Clean text for TTS (remove markdown, emojis for cleaner speech)
  const cleanTextForTTS = useCallback((rawText) => {
    if (!rawText) return '';
    
    let cleaned = rawText
      // Remove markdown bold/italic
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      // Remove markdown headers
      .replace(/^#+\s*/gm, '')
      // Remove bullet points but keep content
      .replace(/^[-•]\s*/gm, '')
      // Remove numbered lists formatting
      .replace(/^\d+\.\s*/gm, '')
      // Keep emojis but add pause after them
      // Actually, let's keep emojis - they add character
      // Remove extra whitespace
      .replace(/\s+/g, ' ')
      .trim();
    
    return cleaned;
  }, []);
  
  // Split text into sentences for highlighting
  const splitIntoSentences = useCallback((text) => {
    if (!text) return [];
    const cleaned = cleanTextForTTS(text);
    // Split on sentence-ending punctuation
    const parts = cleaned.split(/(?<=[.!?])\s+/);
    return parts.filter(s => s.trim().length > 0);
  }, [cleanTextForTTS]);
  
  // Generate and play audio
  const generateAndPlay = useCallback(async () => {
    if (!text || text.trim().length < 1) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const cleanedText = cleanTextForTTS(text);
      const isReplay = replayCount > 0;
      
      const response = await fetch(`${API_URL}/api/voice/generate-sentences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: cleanedText,
          speed: speed,
          is_replay: isReplay
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate audio');
      }
      
      const data = await response.json();
      
      // Create audio from base64
      const audioSrc = `data:audio/mp3;base64,${data.audio_base64}`;
      
      if (audioRef.current) {
        audioRef.current.src = audioSrc;
        audioRef.current.playbackRate = 1.0; // Speed is handled server-side
        
        // Set up sentence highlighting timers
        setSentences(data.sentences.map(s => s.text));
        
        // Clear any existing timers
        sentenceTimersRef.current.forEach(t => clearTimeout(t));
        sentenceTimersRef.current = [];
        
        // Schedule sentence highlights
        data.sentences.forEach((sentence, index) => {
          const timer = setTimeout(() => {
            setCurrentSentenceIndex(index);
            if (onSentenceChange) {
              onSentenceChange(index, sentence.text);
            }
          }, sentence.start_ms);
          sentenceTimersRef.current.push(timer);
        });
        
        await audioRef.current.play();
        setIsPlaying(true);
        if (onPlayStart) onPlayStart();
      }
      
    } catch (err) {
      console.error('Voice generation error:', err);
      setError('Could not generate voice. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [text, speed, replayCount, cleanTextForTTS, onPlayStart, onSentenceChange]);
  
  // Handle play/pause toggle
  const togglePlay = useCallback(() => {
    if (isLoading) return;
    
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else if (audioRef.current && audioRef.current.src && audioRef.current.currentTime > 0) {
      // Resume existing audio
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      // Generate new audio
      generateAndPlay();
    }
  }, [isPlaying, isLoading, generateAndPlay]);
  
  // Handle replay
  const handleReplay = useCallback(() => {
    setReplayCount(prev => prev + 1);
    setCurrentSentenceIndex(-1);
    
    // Clear timers
    sentenceTimersRef.current.forEach(t => clearTimeout(t));
    sentenceTimersRef.current = [];
    
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.src = ''; // Force regeneration
    }
    
    generateAndPlay();
  }, [generateAndPlay]);
  
  // Handle speed change
  const handleSpeedChange = useCallback((newSpeed) => {
    setSpeed(newSpeed);
    setShowSettings(false);
    // If audio is loaded, regenerate with new speed
    if (audioRef.current && audioRef.current.src) {
      setCurrentSentenceIndex(-1);
      audioRef.current.src = '';
    }
  }, []);
  
  // Audio ended handler
  useEffect(() => {
    const audio = audioRef.current;
    
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentSentenceIndex(-1);
      sentenceTimersRef.current.forEach(t => clearTimeout(t));
      sentenceTimersRef.current = [];
      if (onPlayEnd) onPlayEnd();
    };
    
    if (audio) {
      audio.addEventListener('ended', handleEnded);
      return () => audio.removeEventListener('ended', handleEnded);
    }
  }, [onPlayEnd]);
  
  // Auto-play on mount if enabled
  useEffect(() => {
    if (autoPlay && text) {
      const timer = setTimeout(() => {
        generateAndPlay();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoPlay, text, generateAndPlay]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      sentenceTimersRef.current.forEach(t => clearTimeout(t));
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);
  
  // Initialize sentences for highlighting display
  useEffect(() => {
    if (text) {
      setSentences(splitIntoSentences(text));
    }
  }, [text, splitIntoSentences]);
  
  const speedOptions = [
    { value: 0.75, label: '0.75x', description: 'Slower' },
    { value: 1.0, label: '1x', description: 'Normal' },
    { value: 1.25, label: '1.25x', description: 'Faster' }
  ];
  
  return (
    <div className={`arethera-voice ${className}`}>
      {/* Hidden audio element */}
      <audio ref={audioRef} preload="none" />
      
      {/* Main control bar */}
      <div className="flex items-center gap-2 bg-indigo-900/30 rounded-lg px-3 py-2 border border-indigo-500/30">
        {/* Listen/Pause Button */}
        <button
          onClick={togglePlay}
          disabled={isLoading}
          className={`
            flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium
            transition-all duration-200
            ${isLoading 
              ? 'bg-indigo-500/30 text-indigo-300 cursor-wait' 
              : isPlaying
                ? 'bg-amber-500/80 text-white hover:bg-amber-500'
                : 'bg-indigo-500/80 text-white hover:bg-indigo-500'
            }
          `}
          data-testid="arethera-listen-btn"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Loading...</span>
            </>
          ) : isPlaying ? (
            <>
              <Pause className="w-4 h-4" />
              <span>Pause</span>
            </>
          ) : (
            <>
              <Volume2 className="w-4 h-4" />
              <span>Listen</span>
            </>
          )}
        </button>
        
        {/* Replay Button - shown after first play */}
        {(replayCount > 0 || (audioRef.current && audioRef.current.src)) && (
          <button
            onClick={handleReplay}
            disabled={isLoading}
            className="p-1.5 rounded-md text-indigo-300 hover:text-white hover:bg-indigo-500/30 transition-colors"
            title="Replay (slightly slower for better understanding)"
            data-testid="arethera-replay-btn"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
        
        {/* Speed Settings */}
        <div className="relative ml-auto">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-1.5 rounded-md text-indigo-300 hover:text-white hover:bg-indigo-500/30 transition-colors flex items-center gap-1"
            data-testid="arethera-settings-btn"
          >
            <Settings className="w-4 h-4" />
            <span className="text-xs">{speed}x</span>
          </button>
          
          {showSettings && (
            <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-indigo-500/30 rounded-lg shadow-lg z-10 min-w-32">
              <div className="p-2 text-xs text-indigo-300 border-b border-indigo-500/20">
                Speed
              </div>
              {speedOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => handleSpeedChange(option.value)}
                  className={`
                    w-full px-3 py-2 text-left text-sm transition-colors
                    ${speed === option.value 
                      ? 'bg-indigo-500/30 text-white' 
                      : 'text-indigo-200 hover:bg-indigo-500/20'
                    }
                  `}
                >
                  <span className="font-medium">{option.label}</span>
                  <span className="text-xs text-indigo-400 ml-2">{option.description}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Error display */}
      {error && (
        <div className="mt-2 text-sm text-red-400 bg-red-500/10 rounded px-3 py-1.5">
          {error}
        </div>
      )}
      
      {/* Highlighted text display (only when playing) */}
      {isPlaying && sentences.length > 0 && (
        <div className="mt-3 p-3 bg-slate-900/50 rounded-lg border border-indigo-500/20">
          <div className="text-sm leading-relaxed">
            {sentences.map((sentence, index) => (
              <span
                key={index}
                className={`
                  transition-all duration-300
                  ${index === currentSentenceIndex 
                    ? 'text-amber-300 bg-amber-500/20 px-1 rounded' 
                    : index < currentSentenceIndex
                      ? 'text-indigo-300/60'
                      : 'text-indigo-200'
                  }
                `}
              >
                {sentence}{' '}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AretHeraVoice;
